import type { ParsingError } from "../../types";
import type { ConverterOutput, ConverterResult } from "../types";
import { ConverterErrorType } from "../types";

function mergeSnapshotMetadata(
  a: ConverterResult["snapshots"][number],
  b: ConverterResult["snapshots"][number],
) {
  const errors: ParsingError<ConverterErrorType>[] = [];

  /**
   * AcquisitionStartDate missmatch
   */
  if (
    a.acquisitionStartDate &&
    b.acquisitionStartDate &&
    a.acquisitionStartDate !== b.acquisitionStartDate
  ) {
    errors.push({
      kind: ConverterErrorType.SNAPSHOT_METADATA_MISSMATCH,
      explain: "conflict in snapshot acquisition start date",
      location: { file: "", line: 0 },
      data: {
        hint: b.date,
        value: [a.acquisitionStartDate, b.acquisitionStartDate],
      },
    });
  }

  /** AcquisitionEndDate missmatch */
  if (
    a.acquisitionEndDate &&
    b.acquisitionEndDate &&
    a.acquisitionEndDate !== b.acquisitionEndDate
  ) {
    errors.push({
      kind: ConverterErrorType.SNAPSHOT_METADATA_MISSMATCH,
      explain: "conflict in snapshot acquisition end date",
      location: { file: "", line: 0 },
      data: {
        hint: b.date,
        value: [a.acquisitionEndDate, b.acquisitionEndDate],
      },
    });
  }
  /** Label missmatch */
  if (a.label && b.label && a.label !== b.label) {
    errors.push({
      kind: ConverterErrorType.SNAPSHOT_METADATA_MISSMATCH,
      explain: "conflict in snapshot label",
      location: { file: "", line: 0 },
      data: {
        hint: b.date,
        value: [a.label, b.label],
      },
    });
  }
  /** Group missmatch */
  if (a.group && b.group && a.group !== b.group) {
    errors.push({
      kind: ConverterErrorType.SNAPSHOT_METADATA_MISSMATCH,
      explain: "conflict in snapshot group",
      location: { file: "", line: 0 },
      data: {
        hint: b.date,
        value: [a.group, b.group],
      },
    });
  }

  /**
   * Merge metadata
   */
  return {
    result: {
      acquisitionStartDate: a.acquisitionStartDate || b.acquisitionStartDate,
      acquisitionEndDate: a.acquisitionEndDate || b.acquisitionEndDate,
      label: a.label || b.label,
      group: a.group || b.group,
    },
    errors,
  };
}

function mergeSnapshotSensorValues(
  a: ConverterResult["snapshots"][number]["sensorValues"],
  b: ConverterResult["snapshots"][number]["sensorValues"],
): {
  result: ConverterResult["snapshots"][number]["sensorValues"];
  errors: ParsingError<ConverterErrorType>[];
} {
  const result: ConverterResult["snapshots"][number]["sensorValues"] = [];
  const errors: ParsingError<ConverterErrorType>[] = [];
  /**
   * Find the longest array to compare to
   */
  const source = a.length > b.length ? a : b;
  const input = a.length > b.length ? b : a;

  for (const sourceValue of source) {
    for (const inputValue of input) {
      /**
       * If sensorTag match, perform verifications
       */
      if (sourceValue.sensorTag === inputValue.sensorTag) {
        /**
         * Matching but values diverging, store error
         */
        if (sourceValue.mean && inputValue.mean && sourceValue.mean !== inputValue.mean) {
          errors.push({
            kind: ConverterErrorType.CONFLICTING_SENSOR_VALUE,
            explain: "conflict in sensor mean values",
            location: { file: "", line: 0 },
            data: {
              key: sourceValue.sensorTag,
              value: [sourceValue.mean, inputValue.mean],
            },
          });
        }
        if (sourceValue.std && inputValue.std && sourceValue.std !== inputValue.std) {
          errors.push({
            kind: ConverterErrorType.CONFLICTING_SENSOR_VALUE,
            explain: "conflict in sensor std values",
            location: { file: "", line: 0 },
            data: {
              key: sourceValue.sensorTag,
              value: [sourceValue.std, inputValue.std],
            },
          });
        }
        /**
         * Assigne result
         */
        sourceValue.mean = sourceValue.mean || inputValue.mean;
        sourceValue.std = sourceValue.std || inputValue.std;
        continue;
      }

      if (result.some(v => v.sensorTag === inputValue.sensorTag)) continue;
      result.push(inputValue);
    }
    if (result.some(v => v.sensorTag === sourceValue.sensorTag)) continue;
    result.push(sourceValue);
  }

  return { result, errors };
}

/**
 * Utility function to merge multiple converted outputs into one.
 * Keep encoutered errors if any conflict between outputs.
 * @param outputs list of outputs to merge
 */
export function mergeConvertedOutputs(convertedOutputs: ConverterOutput[]): ConverterOutput {
  const mergingErrors: ParsingError<ConverterErrorType>[] = [];
  const existingErrors = convertedOutputs.flatMap(({ errors }) => errors);
  const merge: ConverterResult = {
    sourceId: undefined,
    snapshots: [],
  };

  merge_loop: for (const output of convertedOutputs) {
    /**
     * Check for diverging sourceId, if so, stop merging.
     */
    if (merge.sourceId && output.result.sourceId && merge.sourceId !== output.result.sourceId) {
      mergingErrors.push({
        kind: ConverterErrorType.CONFLICTING_SOURCE_ID,
        explain: "conflict in snapshot source id",
        location: { file: "", line: 0 },
        data: {
          value: [merge.sourceId, output.result.sourceId],
        },
      });
      break merge_loop;
    }

    merge.sourceId = merge.sourceId || output.result.sourceId;

    /**
     * Assign new snapshot for the first round.
     */
    if (merge.snapshots.length === 0) {
      merge.snapshots = output.result.snapshots;
      continue merge_loop;
    }

    merge_snapshot_loop: for (const snapshot of output.result.snapshots) {
      /**
       * Find existing snapshot for a given date of the input we want to merge.
       */
      const existingMergedSnapshot = merge.snapshots.find(({ date }) => date === snapshot.date);

      /**
       * If no existing snapshot, just add it to the merged output.
       */
      if (!existingMergedSnapshot) {
        merge.snapshots.push(snapshot);
        continue merge_snapshot_loop;
      }

      /**
       * If existing snapshot, merge metadata and find possible conflicts.
       */
      const { result: mergedMetadata, errors: metadataErrors } = mergeSnapshotMetadata(
        existingMergedSnapshot,
        snapshot,
      );

      /** Exit on metadata errors */
      if (metadataErrors.length > 0) {
        mergingErrors.push(...metadataErrors);
        break merge_loop;
      }

      /**
       * Merge sensorValues and find possible conflicts.
       */
      const { result, errors } = mergeSnapshotSensorValues(
        existingMergedSnapshot.sensorValues,
        snapshot.sensorValues,
      );

      /** Exit on sensorValues errors */
      if (errors.length > 0) {
        mergingErrors.push(...errors);
        break merge_loop;
      }

      /** Assign merged values */
      existingMergedSnapshot.acquisitionStartDate = mergedMetadata.acquisitionStartDate;
      existingMergedSnapshot.acquisitionEndDate = mergedMetadata.acquisitionEndDate;
      existingMergedSnapshot.label = mergedMetadata.label;
      existingMergedSnapshot.group = mergedMetadata.group;
      existingMergedSnapshot.sensorValues = result;
    }
  }

  return {
    result: merge,
    errors: [...existingErrors, ...mergingErrors],
  };
}
